import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCogs,
  faRefresh,
  faEdit,
} from '@fortawesome/free-solid-svg-icons';
import { streetlearnApi } from '../api';
import { showToast } from '../../components/toast';

const StreetlearnExamSettings = () => {
  const [configs, setConfigs] = useState([]);
  const [poolSummary, setPoolSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingConfig, setEditingConfig] = useState(null);
  const [formData, setFormData] = useState({
    question_count: 20,
    pass_pct: 70,
    time_limit_min: 30,
    cooldown_hours: 24,
    max_attempts: 0,
    require_lessons_complete: true,
  });
  const [saving, setSaving] = useState(false);

  // Filters
  const [search, setSearch] = useState('');
  const [healthFilter, setHealthFilter] = useState('');
  const [prereqFilter, setPrereqFilter] = useState('');

  const loadData = async () => {
    try {
      setLoading(true);
      const [cfgData, poolData] = await Promise.all([
        streetlearnApi.getExamConfigs(),
        streetlearnApi.getQuestionPoolSummary(),
      ]);
      setConfigs(cfgData || []);
      setPoolSummary(poolData || []);
    } catch (err) {
      console.error('Failed to load exam configs:', err);
      showToast.error(err?.response?.data?.message || 'Failed to load exam configs.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleEdit = (item) => {
    setEditingConfig(item);
    setFormData({
      question_count: item.question_count ?? 20,
      pass_pct: item.pass_pct ?? 70,
      time_limit_min: item.time_limit_min ?? 30,
      cooldown_hours: item.cooldown_hours ?? 24,
      max_attempts: item.max_attempts ?? 0,
      require_lessons_complete: item.require_lessons_complete !== false,
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!editingConfig) return;

    try {
      setSaving(true);
      await streetlearnApi.updateExamConfig(editingConfig.level_id, formData);
      showToast.success('Exam configuration updated successfully!');
      setEditingConfig(null);
      loadData();
    } catch (err) {
      console.error(err);
      showToast.error(err?.response?.data?.message || 'Failed to update configuration.');
    } finally {
      setSaving(false);
    }
  };

  // Map pool health to level id
  const poolMap = new Map(poolSummary.map((p) => [p.level_id, p]));

  // Filtered configs
  const filteredConfigs = configs.filter((cfg) => {
    if (search) {
      const q = search.toLowerCase();
      const title = (cfg.level?.title || '').toLowerCase();
      const tier = (cfg.level?.tier?.name || '').toLowerCase();
      const num = String(cfg.level?.number || cfg.level_id);
      if (!title.includes(q) && !tier.includes(q) && !num.includes(q)) return false;
    }
    if (prereqFilter) {
      if (prereqFilter === 'required' && !cfg.require_lessons_complete) return false;
      if (prereqFilter === 'none' && cfg.require_lessons_complete) return false;
    }
    if (healthFilter) {
      const pool = poolMap.get(cfg.level_id);
      const h = pool?.pool_health || 'ok';
      if (h !== healthFilter) return false;
    }
    return true;
  });

  return (
    <div className="mt-5">
      {/* Top Header */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
        <div>
          <h4 className="panel-title m-0">
            <FontAwesomeIcon icon={faCogs} className="me-2 text-primary" />
            Certification Exam Rules &amp; Engine Configuration
          </h4>
        </div>
        <div className="d-flex align-items-center flex-wrap">
          <button className="btn btn-default btn-sm" onClick={loadData} title="Refresh">
            <FontAwesomeIcon icon={faRefresh} spin={loading} /> Refresh
          </button>
        </div>
      </div>

      {/* Main Table Panel (Exact Subscriber Module UI) */}
      <div className="panel panel-default with-rounded-corner with-shadow mb-3">
        <div className="panel-body p-0">
          <div className="table-responsive">
            <table className="table table-condensed table-hover bg-white sticky-table m-0">
              <thead>
                <tr>
                  <th>Level &amp; Course</th>
                  <th style={{ minWidth: '120px' }}>Questions Draw</th>
                  <th style={{ minWidth: '120px' }}>Passing Threshold</th>
                  <th style={{ minWidth: '100px' }}>Time Limit</th>
                  <th style={{ minWidth: '120px' }}>Retake Cooldown</th>
                  <th style={{ minWidth: '110px' }}>Max Retakes</th>
                  <th style={{ minWidth: '140px' }}>Prerequisite</th>
                  <th style={{ minWidth: '150px' }}>Exam Pool Health</th>
                  <th style={{ width: '90px', textAlign: 'center' }}>Action</th>
                </tr>
                {/* 🔎 In-Table Search Filter Row (Exact Subscriber Input & Select Style) */}
                <tr>
                  <th>
                    <input
                      type="text"
                      placeholder=""
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '5px',
                        fontSize: '12px',
                      }}
                    />
                  </th>
                  <th></th>
                  <th></th>
                  <th></th>
                  <th></th>
                  <th></th>
                  <th>
                    <select
                      value={prereqFilter}
                      onChange={(e) => setPrereqFilter(e.target.value)}
                      style={{ width: '100%', padding: '5px', fontSize: '12px' }}
                    >
                      <option value="">--select--</option>
                      <option value="required">Required</option>
                      <option value="none">None</option>
                    </select>
                  </th>
                  <th>
                    <select
                      value={healthFilter}
                      onChange={(e) => setHealthFilter(e.target.value)}
                      style={{ width: '100%', padding: '5px', fontSize: '12px' }}
                    >
                      <option value="">--select--</option>
                      <option value="ok">Healthy</option>
                      <option value="tight">Tight</option>
                      <option value="short">Short</option>
                    </select>
                  </th>
                  <th style={{ textAlign: 'center' }}>
                    <button
                      type="button"
                      className="btn btn-default btn-xs"
                      title="Clear all filters"
                      onClick={() => {
                        setSearch('');
                        setPrereqFilter('');
                        setHealthFilter('');
                      }}
                    >
                      Clear
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={9} className="text-center py-4">
                      <FontAwesomeIcon icon={faRefresh} spin className="me-2 text-primary" />
                      Loading exam configurations...
                    </td>
                  </tr>
                ) : filteredConfigs.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="text-center py-4 text-muted">
                      No exam configurations found matching criteria.
                    </td>
                  </tr>
                ) : (
                  filteredConfigs.map((cfg) => {
                    const pool = poolMap.get(cfg.level_id);
                    const poolHealth = pool?.pool_health || 'ok';

                    return (
                      <tr key={cfg.level_id}>
                        <td>
                          <div className="font-bold text-dark">
                            Level {cfg.level?.number || cfg.level_id}: {cfg.level?.title || `Level #${cfg.level_id}`}
                          </div>
                          <small className="text-muted">{cfg.level?.tier?.name || 'Foundation'}</small>
                        </td>
                        <td>
                          <strong className="text-primary">{cfg.question_count}</strong> questions
                        </td>
                        <td>
                          <span className="font-weight-bold text-dark">
                            {cfg.pass_pct}%
                          </span>
                        </td>
                        <td>{cfg.time_limit_min} mins</td>
                        <td>{cfg.cooldown_hours > 0 ? `${cfg.cooldown_hours} hours` : 'Instant'}</td>
                        <td>{cfg.max_attempts > 0 ? `${cfg.max_attempts} attempts` : 'Unlimited'}</td>
                        <td>
                          {cfg.require_lessons_complete ? (
                            <span className="text-primary font-weight-bold">All Lessons Required</span>
                          ) : (
                            <span className="text-muted small">None</span>
                          )}
                        </td>
                        <td>
                          {poolHealth === 'ok' && (
                            <span className="text-success font-weight-bold">Healthy</span>
                          )}
                          {poolHealth === 'tight' && (
                            <span className="text-warning font-weight-bold">Tight (&lt;2x)</span>
                          )}
                          {poolHealth === 'short' && (
                            <span className="text-danger font-weight-bold">Short</span>
                          )}
                          <span className="text-muted small ms-1">
                            ({pool?.exam_questions || 0} Qs)
                          </span>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <button
                            className="btn btn-default btn-xs"
                            onClick={() => handleEdit(cfg)}
                            title="Configure Rules"
                          >
                            <FontAwesomeIcon icon={faEdit} className="me-1" /> Configure
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Edit Config Modal */}
      {editingConfig && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <form onSubmit={handleSave}>
                <div className="modal-header d-flex justify-content-between align-items-center">
                  <h4 className="modal-title font-bold m-0">
                    Configure Level {editingConfig.level?.number || editingConfig.level_id} Exam Rules
                  </h4>
                  <button
                    type="button"
                    className="close"
                    style={{ border: 'none', background: 'transparent', fontSize: '24px', lineHeight: '1', cursor: 'pointer', padding: '0 5px' }}
                    onClick={() => setEditingConfig(null)}
                  >
                    <span>&times;</span>
                  </button>
                </div>

                <div className="modal-body">
                  <p className="text-muted small mb-3">
                    These rules govern random question pool selection, passing marks, time limits, and retake policies for learners taking this certification exam.
                  </p>

                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label className="font-semibold text-sm">
                        Questions Drawn <span className="text-danger">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        min="5"
                        max="100"
                        value={formData.question_count}
                        onChange={(e) =>
                          setFormData({ ...formData, question_count: Number(e.target.value) })
                        }
                        required
                      />
                      <small className="text-muted">Drawn randomly from question pool</small>
                    </div>

                    <div className="col-md-6">
                      <label className="font-semibold text-sm">
                        Passing Threshold (%) <span className="text-danger">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        min="1"
                        max="100"
                        value={formData.pass_pct}
                        onChange={(e) =>
                          setFormData({ ...formData, pass_pct: Number(e.target.value) })
                        }
                        required
                      />
                      <small className="text-muted">e.g. 70 for 70% passing score</small>
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label className="font-semibold text-sm">
                        Time Limit (Minutes) <span className="text-danger">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        min="5"
                        max="300"
                        value={formData.time_limit_min}
                        onChange={(e) =>
                          setFormData({ ...formData, time_limit_min: Number(e.target.value) })
                        }
                        required
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="font-semibold text-sm">
                        Retake Cooldown (Hours)
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        min="0"
                        max="168"
                        value={formData.cooldown_hours}
                        onChange={(e) =>
                          setFormData({ ...formData, cooldown_hours: Number(e.target.value) })
                        }
                      />
                      <small className="text-muted">0 = Immediate retake allowed</small>
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label className="font-semibold text-sm">Max Attempts Allowed</label>
                      <input
                        type="number"
                        className="form-control"
                        min="0"
                        max="20"
                        value={formData.max_attempts}
                        onChange={(e) =>
                          setFormData({ ...formData, max_attempts: Number(e.target.value) })
                        }
                      />
                      <small className="text-muted">0 = Unlimited retakes</small>
                    </div>

                    <div className="col-md-6 d-flex align-items-center pt-3">
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="requireLessonsComplete"
                          checked={formData.require_lessons_complete}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              require_lessons_complete: e.target.checked,
                            })
                          }
                        />
                        <label className="form-check-label font-semibold text-sm" htmlFor="requireLessonsComplete">
                          Require all lessons complete before exam
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-default btn-sm"
                    onClick={() => setEditingConfig(null)}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
                    {saving ? 'Saving...' : 'Save Configuration'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StreetlearnExamSettings;
